// ── Initial service catalogue ─────────────────────────────────────────────────
// Pre-populated from the live InteractiveConsole + MailInRepairsPage data.
// Status: 'active' = confirmed price; 'draft' = price TBD.
//
// imageUrl/imageAlt default to '' here — set them via the admin panel
// or by saving images to /public/images/services/<name>.webp

import type { ServiceEntry } from '../types/services';

const now = new Date().toISOString();

// imageUrl and imageAlt are optional in this helper (default to '').
// All other new fields (whatToBring, upsellIds, isUpsell) are required.
type SvcInput = Omit<ServiceEntry, 'createdAt' | 'updatedAt' | 'imageUrl' | 'imageAlt'> & {
  imageUrl?: string;
  imageAlt?: string;
};

const svc = (partial: SvcInput): ServiceEntry => ({
  imageUrl: '',
  imageAlt: '',
  ...partial,
  createdAt: now,
  updatedAt: now,
});

// Shared "what to bring" strings — keep in sync with customer comms
const WTB = {
  ps5:     'PS5 console only. No cables or controllers needed.',
  ps5pwr:  'PS5 console and power cable.',
  xbox:    'Xbox console only. No cables or controllers needed.',
  xboxpwr: 'Xbox console and power cable.',
  swFull:  'Nintendo Switch console and all Joy-Cons.',
  sw:      'Nintendo Switch console.',
  swDock:  'Nintendo Switch, dock, and HDMI cable if available.',
  ctrl:    'Controller(s) only.',
  laptop:  'Laptop only. Charger helpful but not required.',
  hpc:     'Device only. Charger helpful but not required.',
  nh:      'Device only.',
} as const;

export const INITIAL_SERVICES: ServiceEntry[] = [

  // ── PlayStation 5 ─────────────────────────────────────────────────────────
  svc({
    id: 'ps5-hdmi', sku: 'PS5-HDMI', status: 'active',
    deviceCategory: 'ps5', deviceModels: ['PS5', 'PS5 Slim', 'PS5 Pro'],
    repairCategory: 'repair', repairType: 'HDMI Port Replacement',
    description: 'HDMI port replacement — our most common PS5 repair. Restores full picture and audio output.',
    basePrice: null, rushPrice: null,
    priceTiers: [
      { label: 'PS5 / PS5 Slim', price: 160 },
      { label: 'PS5 Pro',        price: 180 },
    ],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['hdmi', 'no-video', 'ps5'],
    whatToBring: WTB.ps5,
    upsellIds: ['ps5-overheat', 'ps5-cleaning', 'ctrl-drift'],
    isUpsell: false,
  }),

  svc({
    id: 'ps5-overheat', sku: 'PS5-OVHEAT', status: 'active',
    deviceCategory: 'ps5', deviceModels: ['PS5', 'PS5 Slim', 'PS5 Pro'],
    repairCategory: 'repair', repairType: 'Overheating / Thermal Service',
    description: 'Liquid metal reapplication and full thermal cleaning. Stops overheating and fan noise.',
    basePrice: null, rushPrice: null,
    priceTiers: [
      { label: 'PS5 / PS5 Slim', price: 160 },
      { label: 'PS5 Pro',        price: 180 },
    ],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['overheating', 'thermal', 'ps5'],
    whatToBring: WTB.ps5,
    upsellIds: ['ps5-hdmi', 'ps5-cleaning'],
    isUpsell: true,
  }),

  svc({
    id: 'ps5-fan-conn', sku: 'PS5-FANCONN', status: 'active',
    deviceCategory: 'ps5', deviceModels: ['PS5', 'PS5 Slim', 'PS5 Pro'],
    repairCategory: 'repair', repairType: 'Ripped Fan Connector',
    description: 'Fan connector and flex cable repair — common after DIY cleaning attempts.',
    basePrice: 200, rushPrice: null, priceTiers: [],
    turnaround: '2-3 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['fan', 'flex-cable', 'ps5'],
    whatToBring: WTB.ps5,
    upsellIds: ['ps5-overheat', 'ps5-cleaning'],
    isUpsell: false,
  }),

  svc({
    id: 'ps5-power', sku: 'PS5-PWR', status: 'active',
    deviceCategory: 'ps5', deviceModels: ['PS5'],
    repairCategory: 'repair', repairType: 'Power Supply Replacement',
    description: 'Power supply diagnosis and replacement for the original PS5.',
    basePrice: 175, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['power', 'psu', 'ps5'],
    whatToBring: WTB.ps5pwr,
    upsellIds: ['ps5-hdmi'],
    isUpsell: false,
  }),

  svc({
    id: 'ps5slim-power', sku: 'PS5S-PWR', status: 'active',
    deviceCategory: 'ps5', deviceModels: ['PS5 Slim'],
    repairCategory: 'repair', repairType: 'Power Supply Replacement',
    description: 'Power supply diagnosis and replacement for the PS5 Slim.',
    basePrice: 175, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['power', 'psu', 'ps5-slim'],
    whatToBring: WTB.ps5pwr,
    upsellIds: ['ps5-hdmi'],
    isUpsell: false,
  }),

  svc({
    id: 'ps5pro-power', sku: 'PS5P-PWR', status: 'active',
    deviceCategory: 'ps5', deviceModels: ['PS5 Pro'],
    repairCategory: 'repair', repairType: 'Power Supply Replacement',
    description: 'Power supply diagnosis and replacement for the PS5 Pro.',
    basePrice: 235, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['power', 'psu', 'ps5-pro'],
    whatToBring: WTB.ps5pwr,
    upsellIds: ['ps5-hdmi'],
    isUpsell: false,
  }),

  svc({
    id: 'ps5-disc', sku: 'PS5-DISC', status: 'active',
    deviceCategory: 'ps5', deviceModels: ['PS5'],
    repairCategory: 'repair', repairType: 'Disc Drive Repair',
    description: 'Disc drive repair and foreign object removal.',
    basePrice: 175, rushPrice: null, priceTiers: [],
    turnaround: '2-3 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['disc', 'disc-drive', 'ps5'],
    whatToBring: WTB.ps5,
    upsellIds: ['ps5-cleaning'],
    isUpsell: false,
  }),

  svc({
    id: 'ps5-nopower', sku: 'PS5-NOPWR', status: 'active',
    deviceCategory: 'ps5', deviceModels: ['PS5', 'PS5 Slim', 'PS5 Pro'],
    repairCategory: 'repair', repairType: 'No Power Diagnostic',
    description: 'Comprehensive diagnostic and repair for consoles that won\'t power on.',
    basePrice: 75, rushPrice: null, priceTiers: [],
    turnaround: '2-5 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Starting at price — actual cost after diagnosis',
    disclaimer: 'Final price depends on diagnosis', tags: ['no-power', 'dead', 'ps5'],
    whatToBring: WTB.ps5pwr,
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'ps5-cleaning', sku: 'PS5-CLEAN', status: 'active',
    deviceCategory: 'ps5', deviceModels: ['PS5', 'PS5 Slim', 'PS5 Pro'],
    repairCategory: 'repair', repairType: 'Deep Cleaning',
    description: 'Full internal cleaning — dust removal, fan clean, heatsink blow-out.',
    basePrice: 30, rushPrice: null, priceTiers: [],
    turnaround: '1-2 hours', rushTurnaround: '', warranty: '', mailInAvailable: true,
    internalNotes: 'Add-on service — usually bundled with HDMI or thermal', disclaimer: '',
    tags: ['cleaning', 'ps5'],
    whatToBring: WTB.ps5,
    upsellIds: [],
    isUpsell: true,
  }),

  // ── Xbox Series ───────────────────────────────────────────────────────────
  svc({
    id: 'xbox-hdmi', sku: 'XBX-HDMI', status: 'active',
    deviceCategory: 'xbox', deviceModels: ['series x', 'series s'],
    repairCategory: 'repair', repairType: 'HDMI Port Replacement',
    description: 'HDMI port replacement for Xbox Series X and Series S.',
    basePrice: null, rushPrice: null,
    priceTiers: [
      { label: 'Xbox Series X', price: 160 },
      { label: 'Xbox Series S', price: 140 },
    ],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['hdmi', 'no-video', 'xbox'],
    whatToBring: WTB.xbox,
    upsellIds: ['xbox-overheat', 'ctrl-drift'],
    isUpsell: false,
  }),

  svc({
    id: 'xbox-overheat', sku: 'XBX-OVHEAT', status: 'active',
    deviceCategory: 'xbox', deviceModels: ['series x', 'series s'],
    repairCategory: 'repair', repairType: 'Overheating / Thermal Service',
    description: 'Internal cleaning and thermal paste replacement for Xbox Series consoles.',
    basePrice: 140, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['overheating', 'thermal', 'xbox'],
    whatToBring: WTB.xbox,
    upsellIds: ['xbox-hdmi'],
    isUpsell: true,
  }),

  svc({
    id: 'xbox-usb', sku: 'XBX-USB', status: 'active',
    deviceCategory: 'xbox', deviceModels: ['series x', 'series s'],
    repairCategory: 'repair', repairType: 'Ripped Connector / USB Port',
    description: 'Ripped connector and USB port repair.',
    basePrice: 160, rushPrice: null, priceTiers: [],
    turnaround: '2-3 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['usb', 'connector', 'xbox'],
    whatToBring: WTB.xbox,
    upsellIds: ['xbox-hdmi'],
    isUpsell: false,
  }),

  svc({
    id: 'xbox-disc', sku: 'XBX-DISC', status: 'active',
    deviceCategory: 'xbox', deviceModels: ['series x'],
    repairCategory: 'repair', repairType: 'Disc Drive Repair',
    description: 'Disc drive repair and replacement for Xbox Series X.',
    basePrice: 175, rushPrice: null, priceTiers: [],
    turnaround: '2-3 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['disc', 'disc-drive', 'xbox'],
    whatToBring: WTB.xbox,
    upsellIds: ['xbox-overheat'],
    isUpsell: false,
  }),

  svc({
    id: 'xbox-power', sku: 'XBX-PWR', status: 'active',
    deviceCategory: 'xbox', deviceModels: ['series x', 'series s'],
    repairCategory: 'repair', repairType: 'Power Supply Replacement',
    description: 'Power supply diagnosis and replacement.',
    basePrice: null, rushPrice: null,
    priceTiers: [
      { label: 'Xbox Series X', price: 175 },
      { label: 'Xbox Series S', price: 125 },
    ],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['power', 'psu', 'xbox'],
    whatToBring: WTB.xboxpwr,
    upsellIds: ['xbox-hdmi'],
    isUpsell: false,
  }),

  svc({
    id: 'xbox-nopower', sku: 'XBX-NOPWR', status: 'active',
    deviceCategory: 'xbox', deviceModels: ['series x', 'series s'],
    repairCategory: 'repair', repairType: 'No Power Diagnostic',
    description: 'Diagnostic and repair for consoles that won\'t power on.',
    basePrice: 75, rushPrice: null, priceTiers: [],
    turnaround: '2-5 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Starting at price', disclaimer: 'Final price depends on diagnosis',
    tags: ['no-power', 'dead', 'xbox'],
    whatToBring: WTB.xboxpwr,
    upsellIds: [],
    isUpsell: false,
  }),

  // ── Nintendo Switch ───────────────────────────────────────────────────────
  svc({
    id: 'sw-screen', sku: 'SW-SCREEN', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch'],
    repairCategory: 'repair', repairType: 'Screen Replacement (LCD)',
    description: 'LCD replacement with tempered glass protection.',
    basePrice: 120, rushPrice: null, priceTiers: [],
    turnaround: '2-3 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Starting at price', disclaimer: '', tags: ['screen', 'lcd', 'switch'],
    whatToBring: WTB.swFull,
    upsellIds: ['sw-rail', 'ctrl-drift'],
    isUpsell: false,
  }),

  svc({
    id: 'sw-usbc', sku: 'SW-USBC', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch'],
    repairCategory: 'repair', repairType: 'No Power / USB-C Port',
    description: 'Power supply diagnosis and motherboard repair.',
    basePrice: 135, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['usbc', 'power', 'switch'],
    whatToBring: WTB.sw,
    upsellIds: ['sw-battery'],
    isUpsell: false,
  }),

  svc({
    id: 'sw-dock', sku: 'SW-DOCK', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch'],
    repairCategory: 'repair', repairType: "Won't Dock to TV",
    description: 'Docking issues and HDMI output problems.',
    basePrice: 135, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['dock', 'hdmi', 'switch'],
    whatToBring: WTB.swDock,
    upsellIds: ['sw-usbc'],
    isUpsell: false,
  }),

  svc({
    id: 'sw-battery', sku: 'SW-BATT', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch'],
    repairCategory: 'repair', repairType: 'Battery Replacement',
    description: 'Battery replacement for extended gaming sessions.',
    basePrice: 90, rushPrice: null, priceTiers: [],
    turnaround: '2-3 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['battery', 'switch'],
    whatToBring: WTB.sw,
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'sw-rail', sku: 'SW-RAIL', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch'],
    repairCategory: 'repair', repairType: 'JoyCon Rail Replacement',
    description: 'JoyCon rail replacement — left, right, or both.',
    basePrice: 90, rushPrice: null, priceTiers: [],
    turnaround: '2-3 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Price for both rails', disclaimer: '', tags: ['joycon', 'rail', 'switch'],
    whatToBring: WTB.swFull,
    upsellIds: ['ctrl-drift'],
    isUpsell: true,
  }),

  svc({
    id: 'sw-cardreader', sku: 'SW-CARD', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch'],
    repairCategory: 'repair', repairType: 'Card Reader Replacement',
    description: 'SD card or game card reader replacement.',
    basePrice: 90, rushPrice: null, priceTiers: [],
    turnaround: '2-3 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['card-reader', 'switch'],
    whatToBring: WTB.sw,
    upsellIds: [],
    isUpsell: false,
  }),

  // Switch OLED
  svc({
    id: 'sw-oled-screen', sku: 'SWO-SCREEN', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch OLED'],
    repairCategory: 'repair', repairType: 'Screen Replacement (OLED)',
    description: 'OLED replacement with tempered glass protection.',
    basePrice: 210, rushPrice: null, priceTiers: [],
    turnaround: '2-3 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['screen', 'oled', 'switch-oled'],
    whatToBring: WTB.swFull,
    upsellIds: ['sw-oled-rail', 'ctrl-drift'],
    isUpsell: false,
  }),

  svc({
    id: 'sw-oled-usbc', sku: 'SWO-USBC', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch OLED'],
    repairCategory: 'repair', repairType: 'No Power / USB-C Port',
    description: 'Power supply diagnosis and motherboard repair.',
    basePrice: 135, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['usbc', 'power', 'switch-oled'],
    whatToBring: WTB.sw,
    upsellIds: ['sw-oled-battery'],
    isUpsell: false,
  }),

  svc({
    id: 'sw-oled-dock', sku: 'SWO-DOCK', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch OLED'],
    repairCategory: 'repair', repairType: "Won't Dock to TV",
    description: 'Docking issues and HDMI output problems.',
    basePrice: 135, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['dock', 'hdmi', 'switch-oled'],
    whatToBring: WTB.swDock,
    upsellIds: ['sw-oled-usbc'],
    isUpsell: false,
  }),

  svc({
    id: 'sw-oled-battery', sku: 'SWO-BATT', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch OLED'],
    repairCategory: 'repair', repairType: 'Battery Replacement',
    description: 'Battery replacement.',
    basePrice: 90, rushPrice: null, priceTiers: [],
    turnaround: '2-3 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['battery', 'switch-oled'],
    whatToBring: WTB.sw,
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'sw-oled-rail', sku: 'SWO-RAIL', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch OLED'],
    repairCategory: 'repair', repairType: 'JoyCon Rail Replacement',
    description: 'JoyCon rail replacement — left, right, or both.',
    basePrice: 90, rushPrice: null, priceTiers: [],
    turnaround: '2-3 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['joycon', 'rail', 'switch-oled'],
    whatToBring: WTB.swFull,
    upsellIds: ['ctrl-drift'],
    isUpsell: true,
  }),

  svc({
    id: 'sw-oled-card', sku: 'SWO-CARD', status: 'active',
    deviceCategory: 'switch', deviceModels: ['Switch OLED'],
    repairCategory: 'repair', repairType: 'Card Reader Replacement',
    description: 'SD card or game card reader replacement.',
    basePrice: 110, rushPrice: null, priceTiers: [],
    turnaround: '2-3 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['card-reader', 'switch-oled'],
    whatToBring: WTB.sw,
    upsellIds: [],
    isUpsell: false,
  }),

  // Switch 2
  svc({
    id: 'sw2-screen', sku: 'SW2-SCREEN', status: 'active',
    deviceCategory: 'switch', deviceModels: ['switch 2'],
    repairCategory: 'repair', repairType: 'Nintendo Switch 2 LCD Replacement',
    description: 'Replace cracked, damaged, or non-working LCD screens causing display issues, lines, discoloration, or no image.',
    basePrice: 345, rushPrice: null, priceTiers: [],
    turnaround: '2-3 business days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['screen', 'switch-2'],
    whatToBring: WTB.swFull,
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'sw2-usbc', sku: 'SW2-USBC', status: 'active',
    deviceCategory: 'switch', deviceModels: ['switch 2'],
    repairCategory: 'repair', repairType: 'Nintendo Switch 2 USB-C Port Replacement',
    description: 'Repair charging and docking issues caused by a damaged USB-C port.',
    basePrice: 160, rushPrice: null, priceTiers: [],
    turnaround: '3-5 business days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['usbc', 'switch-2'],
    whatToBring: WTB.sw,
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'sw2-battery', sku: 'SW2-BATT', status: 'active',
    deviceCategory: 'switch', deviceModels: ['switch 2'],
    repairCategory: 'repair', repairType: 'Nintendo Switch 2 Battery Replacement',
    description: 'Battery replacement — only replace what\'s needed.',
    basePrice: 110, rushPrice: null, priceTiers: [],
    turnaround: '2-3 business days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['battery', 'switch-2'],
    whatToBring: WTB.sw,
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'sw2-card', sku: 'SW2-CARD', status: 'active',
    deviceCategory: 'switch', deviceModels: ['switch 2'],
    repairCategory: 'repair', repairType: 'Nintendo Switch 2 Game Card Reader',
    description: 'Fix game cartridge reading issues caused by a damaged or non-working game card reader.',
    basePrice: 135, rushPrice: null, priceTiers: [],
    turnaround: '2-3 business days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '', tags: ['card-reader', 'switch-2'],
    whatToBring: WTB.sw,
    upsellIds: [],
    isUpsell: false,
  }),

  // ── Controllers ───────────────────────────────────────────────────────────
  svc({
    id: 'ctrl-drift', sku: 'CTRL-DRIFT', status: 'active',
    deviceCategory: 'controller', deviceModels: [],
    repairCategory: 'repair', repairType: 'Stick Drift Repair',
    description: 'Joystick replacement or GuliKit TMR hall-effect upgrade. Same-day turnaround available.',
    basePrice: 75, rushPrice: null,
    priceTiers: [
      { label: 'Standard Controller', price: 75,   description: 'DualSense, Xbox, Switch Pro (OEM sticks)' },
      { label: 'Pro Controller',       price: 95,   description: 'DualSense Edge, SCUF, Battle Beaver, AIM, HexGaming & similar' },
      { label: 'GuliKit TMR Upgrade',  price: null, description: '+$20 add-on — permanently eliminates drift' },
    ],
    turnaround: '7 days', rushTurnaround: '1-2 days', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Pro controllers = $80 base. GuliKit = +$20 on top of whichever tier.',
    disclaimer: '', tags: ['stick-drift', 'joystick', 'controller'],
    whatToBring: WTB.ctrl,
    upsellIds: ['ctrl-gulikit', 'ctrl-clicky-trig', 'ctrl-paddles', 'ctrl-shells'],
    isUpsell: true,
  }),

  svc({
    id: 'ctrl-clicky-trig', sku: 'CTRL-CTRIG', status: 'active',
    deviceCategory: 'controller', deviceModels: [],
    repairCategory: 'upgrade', repairType: 'Clicky Triggers',
    description: 'Professional clicky trigger installation for enhanced gaming feel.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '2-3 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: set price from spreadsheet', disclaimer: '',
    tags: ['clicky', 'triggers', 'controller'],
    whatToBring: WTB.ctrl,
    upsellIds: ['ctrl-clicky-btn', 'ctrl-paddles'],
    isUpsell: true,
  }),

  svc({
    id: 'ctrl-clicky-btn', sku: 'CTRL-CBTN', status: 'active',
    deviceCategory: 'controller', deviceModels: [],
    repairCategory: 'upgrade', repairType: 'Clicky Buttons',
    description: 'Clicky button installation for better tactile feedback.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '2-3 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: set price from spreadsheet', disclaimer: '',
    tags: ['clicky', 'buttons', 'controller'],
    whatToBring: WTB.ctrl,
    upsellIds: ['ctrl-clicky-trig', 'ctrl-paddles'],
    isUpsell: true,
  }),

  svc({
    id: 'ctrl-paddles', sku: 'CTRL-PADL', status: 'active',
    deviceCategory: 'controller', deviceModels: [],
    repairCategory: 'upgrade', repairType: 'Back Paddles',
    description: 'Professional back paddle installation for remapping without leaving your sticks.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '2-3 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: set price from spreadsheet', disclaimer: '',
    tags: ['paddles', 'back-buttons', 'controller'],
    whatToBring: WTB.ctrl,
    upsellIds: ['ctrl-clicky-trig', 'ctrl-clicky-btn'],
    isUpsell: true,
  }),

  svc({
    id: 'ctrl-battery', sku: 'CTRL-BATT', status: 'active',
    deviceCategory: 'controller', deviceModels: [],
    repairCategory: 'repair', repairType: 'Controller Battery',
    description: 'Controller battery replacement.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '2-3 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: set price from spreadsheet', disclaimer: '',
    tags: ['battery', 'controller'],
    whatToBring: WTB.ctrl,
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'ctrl-shells', sku: 'CTRL-SHEL', status: 'active',
    deviceCategory: 'controller', deviceModels: [],
    repairCategory: 'custom', repairType: 'Custom Shells & Buttons',
    description: 'Custom controller shells, trim, and buttons in various colors and styles.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '2-3 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: set price from spreadsheet', disclaimer: '',
    tags: ['custom', 'shells', 'controller'],
    whatToBring: WTB.ctrl,
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'ctrl-gulikit', sku: 'CTRL-TMR', status: 'active',
    deviceCategory: 'controller', deviceModels: [],
    repairCategory: 'upgrade', repairType: 'GuliKit TMR Upgrade',
    description: 'Upgrade to hall-effect joysticks — permanently eliminates drift.',
    basePrice: 20, rushPrice: null, priceTiers: [],
    turnaround: '7 days', rushTurnaround: '1-2 days', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Add-on only — price is on top of drift repair tier', disclaimer: '',
    tags: ['gulikit', 'tmr', 'hall-effect', 'controller'],
    whatToBring: WTB.ctrl,
    upsellIds: [],
    isUpsell: true,
  }),

  // ── MacBook & Laptops ─────────────────────────────────────────────────────
  svc({
    id: 'lap-screen', sku: 'LAP-SCREEN', status: 'active',
    deviceCategory: 'laptop-macbook', deviceModels: [],
    repairCategory: 'repair', repairType: 'Screen Replacement',
    description: 'LCD/OLED screen replacement for MacBooks and laptops.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Price depends on model — pull from spreadsheet',
    disclaimer: 'Price depends on model', tags: ['screen', 'laptop', 'macbook'],
    whatToBring: WTB.laptop,
    upsellIds: ['lap-clean', 'lap-upgrade', 'lap-data'],
    isUpsell: false,
  }),

  svc({
    id: 'lap-battery', sku: 'LAP-BATT', status: 'active',
    deviceCategory: 'laptop-macbook', deviceModels: [],
    repairCategory: 'repair', repairType: 'Battery Replacement',
    description: 'Battery replacement with 12-month warranty.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '12 months', mailInAvailable: true,
    internalNotes: 'Price depends on model',
    disclaimer: 'Price depends on model', tags: ['battery', 'laptop', 'macbook'],
    whatToBring: WTB.laptop,
    upsellIds: ['lap-clean', 'lap-upgrade'],
    isUpsell: false,
  }),

  svc({
    id: 'lap-clean', sku: 'LAP-CLEAN', status: 'active',
    deviceCategory: 'laptop-macbook', deviceModels: [],
    repairCategory: 'repair', repairType: 'Deep Cleaning',
    description: 'Internal cleaning and thermal paste replacement.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '', mailInAvailable: true,
    internalNotes: 'Price depends on model', disclaimer: '',
    tags: ['cleaning', 'thermal', 'laptop'],
    whatToBring: WTB.laptop,
    upsellIds: ['lap-upgrade'],
    isUpsell: true,
  }),

  svc({
    id: 'lap-upgrade', sku: 'LAP-UPG', status: 'active',
    deviceCategory: 'laptop-macbook', deviceModels: [],
    repairCategory: 'upgrade', repairType: 'RAM / SSD Upgrade',
    description: 'Memory and storage upgrades for better performance.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '3-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Price depends on model + parts',
    disclaimer: 'Price depends on model', tags: ['ram', 'ssd', 'upgrade', 'laptop'],
    whatToBring: WTB.laptop,
    upsellIds: ['lap-data'],
    isUpsell: true,
  }),

  svc({
    id: 'lap-data', sku: 'LAP-DATA', status: 'active',
    deviceCategory: 'laptop-macbook', deviceModels: [],
    repairCategory: 'repair', repairType: 'Data Recovery / Transfer',
    description: 'Data recovery from damaged drives and data transfer services.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '1-3 days', rushTurnaround: '', warranty: '', mailInAvailable: true,
    internalNotes: 'Price depends on complexity',
    disclaimer: 'Price depends on severity of data loss', tags: ['data-recovery', 'laptop'],
    whatToBring: WTB.laptop,
    upsellIds: [],
    isUpsell: true,
  }),

  // ── Handheld PC (Steam Deck / ROG Ally) ──────────────────────────────────
  svc({
    id: 'hpc-screen', sku: 'HPC-SCREEN', status: 'draft',
    deviceCategory: 'handheld-pc',
    deviceModels: ['Steam Deck LCD', 'Steam Deck OLED', 'ROG Ally', 'ROG Ally X'],
    repairCategory: 'repair', repairType: 'Screen Replacement',
    description: 'Full screen replacement for cracked or damaged displays.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price by model from spreadsheet', disclaimer: '',
    tags: ['screen', 'handheld-pc'],
    whatToBring: WTB.hpc,
    upsellIds: ['hpc-battery', 'hpc-halleffect'],
    isUpsell: false,
  }),

  svc({
    id: 'hpc-joystick', sku: 'HPC-JSTK', status: 'draft',
    deviceCategory: 'handheld-pc',
    deviceModels: ['Steam Deck LCD', 'Steam Deck OLED', 'ROG Ally', 'ROG Ally X'],
    repairCategory: 'repair', repairType: 'Joystick Replacement / Stick Drift',
    description: 'Replace worn joysticks or fix stick drift.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet', disclaimer: '',
    tags: ['joystick', 'stick-drift', 'handheld-pc'],
    whatToBring: WTB.hpc,
    upsellIds: ['hpc-halleffect'],
    isUpsell: false,
  }),

  svc({
    id: 'hpc-charging', sku: 'HPC-CHARG', status: 'draft',
    deviceCategory: 'handheld-pc',
    deviceModels: ['Steam Deck LCD', 'Steam Deck OLED', 'ROG Ally', 'ROG Ally X'],
    repairCategory: 'repair', repairType: 'Charging Port Repair (USB-C)',
    description: 'Repair or replace a damaged USB-C charging port.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet', disclaimer: '',
    tags: ['usbc', 'charging', 'handheld-pc'],
    whatToBring: WTB.hpc,
    upsellIds: ['hpc-battery'],
    isUpsell: false,
  }),

  svc({
    id: 'hpc-battery', sku: 'HPC-BATT', status: 'draft',
    deviceCategory: 'handheld-pc',
    deviceModels: ['Steam Deck LCD', 'Steam Deck OLED', 'ROG Ally', 'ROG Ally X'],
    repairCategory: 'repair', repairType: 'Battery Replacement',
    description: 'Replace a degraded battery for longer play sessions.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet', disclaimer: '',
    tags: ['battery', 'handheld-pc'],
    whatToBring: WTB.hpc,
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'hpc-ssd', sku: 'HPC-SSD', status: 'draft',
    deviceCategory: 'handheld-pc',
    deviceModels: ['Steam Deck LCD', 'Steam Deck OLED', 'ROG Ally', 'ROG Ally X'],
    repairCategory: 'upgrade', repairType: 'SSD Storage Upgrade',
    description: 'Upgrade internal storage with a faster, higher-capacity SSD.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet — varies by size', disclaimer: '',
    tags: ['ssd', 'storage', 'handheld-pc'],
    whatToBring: WTB.hpc,
    upsellIds: ['hpc-ram', 'hpc-dualboot'],
    isUpsell: true,
  }),

  svc({
    id: 'hpc-ram', sku: 'HPC-RAM', status: 'draft',
    deviceCategory: 'handheld-pc',
    deviceModels: ['ROG Ally', 'ROG Ally X'],
    repairCategory: 'upgrade', repairType: 'RAM Upgrade',
    description: 'Increase RAM — ROG Ally supports up to 32GB.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Steam Deck RAM is soldered — not upgradeable. ROG Ally only.',
    disclaimer: '', tags: ['ram', 'upgrade', 'handheld-pc'],
    whatToBring: WTB.hpc,
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'hpc-halleffect', sku: 'HPC-HALL', status: 'draft',
    deviceCategory: 'handheld-pc',
    deviceModels: ['Steam Deck LCD', 'Steam Deck OLED', 'ROG Ally', 'ROG Ally X'],
    repairCategory: 'upgrade', repairType: 'Hall Effect Joystick Upgrade',
    description: 'Upgrade to hall effect joysticks to permanently eliminate stick drift.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet', disclaimer: '',
    tags: ['hall-effect', 'joystick', 'handheld-pc'],
    whatToBring: WTB.hpc,
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'hpc-dualboot', sku: 'HPC-DBOOT', status: 'draft',
    deviceCategory: 'handheld-pc',
    deviceModels: ['Steam Deck LCD', 'Steam Deck OLED', 'ROG Ally', 'ROG Ally X'],
    repairCategory: 'upgrade', repairType: 'Dual Boot OS Setup',
    description: 'Set up dual boot for maximum OS flexibility.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet', disclaimer: '',
    tags: ['dual-boot', 'os', 'handheld-pc'],
    whatToBring: WTB.hpc,
    upsellIds: [],
    isUpsell: false,
  }),

  // ── Nintendo Handheld (3DS/DS/GB/PSP/Vita) ───────────────────────────────
  svc({
    id: 'nh-screen', sku: 'NH-SCREEN', status: 'draft',
    deviceCategory: 'nintendo-handheld', deviceModels: [],
    repairCategory: 'repair', repairType: 'Screen Replacement',
    description: 'Upper or lower screen replacement.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price by model from spreadsheet', disclaimer: '',
    tags: ['screen', 'nintendo-handheld'],
    whatToBring: WTB.nh,
    upsellIds: ['nh-battery', 'nh-ips'],
    isUpsell: false,
  }),

  svc({
    id: 'nh-charging', sku: 'NH-CHARG', status: 'draft',
    deviceCategory: 'nintendo-handheld', deviceModels: [],
    repairCategory: 'repair', repairType: 'Charging Port Repair',
    description: 'Repair or replace the charging port.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet', disclaimer: '',
    tags: ['charging', 'nintendo-handheld'],
    whatToBring: WTB.nh,
    upsellIds: ['nh-battery'],
    isUpsell: false,
  }),

  svc({
    id: 'nh-battery', sku: 'NH-BATT', status: 'draft',
    deviceCategory: 'nintendo-handheld', deviceModels: [],
    repairCategory: 'repair', repairType: 'Battery Replacement',
    description: 'Replace a degraded or dead battery.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet', disclaimer: '',
    tags: ['battery', 'nintendo-handheld'],
    whatToBring: WTB.nh,
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'nh-buttons', sku: 'NH-BTNS', status: 'draft',
    deviceCategory: 'nintendo-handheld', deviceModels: [],
    repairCategory: 'repair', repairType: 'Button Repair',
    description: 'Fix sticky, unresponsive, or damaged buttons.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet', disclaimer: '',
    tags: ['buttons', 'nintendo-handheld'],
    whatToBring: WTB.nh,
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'nh-ips', sku: 'NH-IPS', status: 'draft',
    deviceCategory: 'nintendo-handheld', deviceModels: [],
    repairCategory: 'upgrade', repairType: 'IPS Screen Upgrade',
    description: 'Upgrade to a sharper, more vivid IPS display.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price by model — not all models same',
    disclaimer: 'Compatibility varies by model and revision',
    tags: ['ips', 'screen-upgrade', 'nintendo-handheld'],
    whatToBring: WTB.nh,
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'nh-usbc', sku: 'NH-USBC', status: 'draft',
    deviceCategory: 'nintendo-handheld', deviceModels: [],
    repairCategory: 'upgrade', repairType: 'USB-C Charging Upgrade',
    description: 'Upgrade from proprietary connector to USB-C.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet', disclaimer: '',
    tags: ['usbc', 'charging-upgrade', 'nintendo-handheld'],
    whatToBring: WTB.nh,
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'nh-shell', sku: 'NH-SHEL', status: 'draft',
    deviceCategory: 'nintendo-handheld', deviceModels: [],
    repairCategory: 'upgrade', repairType: 'Custom Shell Replacement',
    description: 'Swap your shell for a custom color or design.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'TODO: price from spreadsheet', disclaimer: '',
    tags: ['shell', 'custom', 'nintendo-handheld'],
    whatToBring: WTB.nh,
    upsellIds: [],
    isUpsell: false,
  }),

  // ── iPhone ────────────────────────────────────────────────────────────────

  svc({
    id: 'iph-screen', sku: 'IPH-SCREEN', status: 'active',
    deviceCategory: 'iphone',
    deviceModels: [
      'iPhone 16 Pro Max', 'iPhone 16 Pro', 'iPhone 16 Plus', 'iPhone 16',
      'iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15',
      'iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14',
      'iPhone 13 Pro Max', 'iPhone 13 Pro', 'iPhone 13', 'iPhone 13 mini',
      'iPhone 12 Pro Max', 'iPhone 12 Pro', 'iPhone 12', 'iPhone 12 mini',
      'iPhone 11 Pro Max', 'iPhone 11 Pro', 'iPhone 11',
      'iPhone XS Max', 'iPhone XS', 'iPhone XR', 'iPhone X',
      'iPhone SE (3rd gen)', 'iPhone SE (2nd gen)',
    ],
    repairCategory: 'repair', repairType: 'Screen Replacement',
    description: 'OLED/LCD screen replacement using high-quality display panels. Proper calibration and brightness restored.',
    basePrice: null, rushPrice: null,
    priceTiers: [
      { label: 'iPhone 16 Pro Max', price: 399 },
      { label: 'iPhone 16 Pro', price: 349 },
      { label: 'iPhone 16 Plus', price: 279 },
      { label: 'iPhone 16', price: 249 },
      { label: 'iPhone 15 Pro Max', price: 329 },
      { label: 'iPhone 15 Pro', price: 289 },
      { label: 'iPhone 15 Plus', price: 249 },
      { label: 'iPhone 15', price: 219 },
      { label: 'iPhone 14 Pro Max', price: 279 },
      { label: 'iPhone 14 Pro', price: 249 },
      { label: 'iPhone 14 Plus', price: 219 },
      { label: 'iPhone 14', price: 189 },
      { label: 'iPhone 13 series', price: 169 },
      { label: 'iPhone 12 series', price: 149 },
      { label: 'iPhone 11 series', price: 129 },
      { label: 'iPhone XR / XS / X', price: 119 },
      { label: 'iPhone SE', price: 89 },
    ],
    turnaround: 'Same day (most models)', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Use quality OLED panels only — no bargain aftermarket screens',
    disclaimer: 'Pricing varies by exact model. Final price confirmed after inspection.',
    tags: ['screen', 'iphone', 'oled', 'lcd', 'display'],
    whatToBring: 'iPhone only. No charger or case needed.',
    upsellIds: ['iph-tempered-glass', 'iph-privacy-glass', 'iph-prot-pro-ultra', 'iph-prot-pro-matte', 'iph-battery'],
    isUpsell: false,
  }),

  svc({
    id: 'iph-battery', sku: 'IPH-BATT', status: 'active',
    deviceCategory: 'iphone',
    deviceModels: [
      'iPhone 16 Pro Max', 'iPhone 16 Pro', 'iPhone 16 Plus', 'iPhone 16',
      'iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15',
      'iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14',
      'iPhone 13 Pro Max', 'iPhone 13 Pro', 'iPhone 13', 'iPhone 13 mini',
      'iPhone 12 Pro Max', 'iPhone 12 Pro', 'iPhone 12', 'iPhone 12 mini',
      'iPhone 11 Pro Max', 'iPhone 11 Pro', 'iPhone 11',
      'iPhone XR', 'iPhone XS Max', 'iPhone XS', 'iPhone X',
      'iPhone SE (3rd gen)', 'iPhone SE (2nd gen)',
    ],
    repairCategory: 'repair', repairType: 'Battery Replacement',
    description: 'Battery replacement using quality cells. Restores proper battery life and eliminates unexpected shutdowns.',
    basePrice: 99, rushPrice: null, priceTiers: [],
    turnaround: 'Same day', rushTurnaround: '', warranty: '12 months', mailInAvailable: true,
    internalNotes: 'Use quality battery cells — no bargain knockoffs',
    disclaimer: 'Price may vary on select Pro Max models.',
    tags: ['battery', 'iphone', 'power'],
    whatToBring: 'iPhone only.',
    upsellIds: ['iph-tempered-glass', 'iph-prot-pro-ultra'],
    isUpsell: false,
  }),

  svc({
    id: 'iph-charging', sku: 'IPH-CHRG', status: 'active',
    deviceCategory: 'iphone',
    deviceModels: [],
    repairCategory: 'repair', repairType: 'Charging Port Repair',
    description: 'Lightning or USB-C port replacement or cleaning. Resolves slow/no charging and intermittent connection issues.',
    basePrice: 89, rushPrice: null, priceTiers: [],
    turnaround: 'Same day', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '',
    tags: ['charging', 'usbc', 'lightning', 'iphone', 'port'],
    whatToBring: 'iPhone only. Bring your charger cable if the issue is cable-specific.',
    upsellIds: ['iph-battery'],
    isUpsell: false,
  }),

  svc({
    id: 'iph-back-glass', sku: 'IPH-BGLASS', status: 'active',
    deviceCategory: 'iphone',
    deviceModels: [
      'iPhone 16 Pro Max', 'iPhone 16 Pro', 'iPhone 16 Plus', 'iPhone 16',
      'iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15',
      'iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14',
      'iPhone 13 Pro Max', 'iPhone 13 Pro', 'iPhone 13',
      'iPhone 12 Pro Max', 'iPhone 12 Pro', 'iPhone 12',
      'iPhone 11 Pro Max', 'iPhone 11 Pro', 'iPhone 11',
      'iPhone XS Max', 'iPhone XS', 'iPhone X',
    ],
    repairCategory: 'repair', repairType: 'Back Glass Repair',
    description: 'Cracked back glass replacement. Restores water resistance seal and appearance.',
    basePrice: null, rushPrice: null,
    priceTiers: [
      { label: 'iPhone 16 / 15 Pro Max', price: 199 },
      { label: 'iPhone 16 / 15 Pro', price: 179 },
      { label: 'iPhone 16 / 15 Plus', price: 159 },
      { label: 'iPhone 16 / 15', price: 149 },
      { label: 'iPhone 14 series', price: 139 },
      { label: 'iPhone 13 series', price: 119 },
      { label: 'iPhone 12 / 11 / X series', price: 99 },
    ],
    turnaround: 'Same day', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: 'Price varies by model.',
    tags: ['back-glass', 'iphone', 'cracked'],
    whatToBring: 'iPhone only.',
    upsellIds: ['iph-tempered-glass', 'iph-prot-pro-ultra'],
    isUpsell: false,
  }),

  svc({
    id: 'iph-camera', sku: 'IPH-CAM', status: 'active',
    deviceCategory: 'iphone',
    deviceModels: [],
    repairCategory: 'repair', repairType: 'Camera Repair / Replacement',
    description: 'Front or rear camera module replacement. Resolves blurry images, black camera, or autofocus failure.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '2-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Price varies by model and front/rear. Get a quote.', disclaimer: 'Price confirmed after diagnosis.',
    tags: ['camera', 'iphone', 'front-camera', 'rear-camera'],
    whatToBring: 'iPhone only.',
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'iph-speaker', sku: 'IPH-SPK', status: 'active',
    deviceCategory: 'iphone',
    deviceModels: [],
    repairCategory: 'repair', repairType: 'Speaker / Earpiece Repair',
    description: 'Earpiece, bottom speaker, or microphone repair. Restores call audio, media sound, and voice recording.',
    basePrice: 79, rushPrice: null, priceTiers: [],
    turnaround: '1-2 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '',
    tags: ['speaker', 'earpiece', 'microphone', 'iphone', 'audio'],
    whatToBring: 'iPhone only.',
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'iph-water', sku: 'IPH-WATER', status: 'active',
    deviceCategory: 'iphone',
    deviceModels: [],
    repairCategory: 'repair', repairType: 'Water Damage Diagnostics',
    description: 'Full internal inspection, liquid damage assessment, and cleaning. We\'ll give you an honest assessment of repair viability and data recovery options.',
    basePrice: 49, rushPrice: null, priceTiers: [],
    turnaround: '1-2 hours diagnostic', rushTurnaround: '', warranty: '', mailInAvailable: true,
    internalNotes: 'Diagnostic fee applies toward repair if customer proceeds. Do not promise success.', disclaimer: 'Water damage repair success cannot be guaranteed. Diagnostic fee does not guarantee repairability.',
    tags: ['water-damage', 'iphone', 'liquid', 'diagnostic'],
    whatToBring: 'iPhone only. Do NOT charge or power on a wet device — bring it off.',
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'iph-diagnostic', sku: 'IPH-DIAG', status: 'active',
    deviceCategory: 'iphone',
    deviceModels: [],
    repairCategory: 'repair', repairType: 'Diagnostic Service',
    description: 'Full hardware and software diagnostic to identify what\'s wrong with your iPhone. Fee applies toward repair.',
    basePrice: 29, rushPrice: null, priceTiers: [],
    turnaround: '30-60 minutes', rushTurnaround: '', warranty: '', mailInAvailable: true,
    internalNotes: 'Diagnostic fee credited toward repair cost if customer proceeds.',
    disclaimer: 'Diagnostic fee applies toward repair cost if work is authorized.',
    tags: ['diagnostic', 'iphone'],
    whatToBring: 'iPhone only. Know your Apple ID password if possible.',
    upsellIds: [],
    isUpsell: false,
  }),

  // ── iPhone Screen Protection (Upsells) ────────────────────────────────────

  svc({
    id: 'iph-tempered-glass', sku: 'IPH-TGLASS', status: 'active',
    deviceCategory: 'iphone', deviceModels: [],
    repairCategory: 'repair', repairType: 'Tempered Glass Screen Protector',
    description: 'Professional tempered glass installation. Applied while your device is here — perfect time to protect your new screen.',
    basePrice: 20, rushPrice: null, priceTiers: [],
    turnaround: 'Instant add-on', rushTurnaround: '', warranty: '30 days', mailInAvailable: false,
    internalNotes: '', disclaimer: '',
    tags: ['screen-protector', 'tempered-glass', 'iphone', 'protection'],
    whatToBring: '',
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'iph-privacy-glass', sku: 'IPH-PGLASS', status: 'active',
    deviceCategory: 'iphone', deviceModels: [],
    repairCategory: 'repair', repairType: 'Privacy Tempered Glass',
    description: 'Privacy screen protector that blocks side viewing. Installed while your device is here.',
    basePrice: 25, rushPrice: null, priceTiers: [],
    turnaround: 'Instant add-on', rushTurnaround: '', warranty: '30 days', mailInAvailable: false,
    internalNotes: '', disclaimer: '',
    tags: ['privacy-glass', 'screen-protector', 'iphone', 'protection'],
    whatToBring: '',
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'iph-prot-pro-ultra', sku: 'IPH-PPU2', status: 'active',
    deviceCategory: 'iphone', deviceModels: [],
    repairCategory: 'repair', repairType: 'ProtectionPro Ultra 2 Film',
    description: 'Premium self-healing TPU film by Madico (ProtectionPro Ultra 2). Virtually invisible, absorbs impacts, self-heals minor scratches.',
    basePrice: 45, rushPrice: null, priceTiers: [],
    turnaround: 'Installed at appointment', rushTurnaround: '', warranty: '90 days', mailInAvailable: false,
    internalNotes: 'ProtectionPro by Madico — Ultra 2 product line', disclaimer: '',
    tags: ['protection-pro', 'madico', 'screen-protector', 'film', 'iphone', 'protection'],
    whatToBring: '',
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'iph-prot-pro-matte', sku: 'IPH-PPM', status: 'active',
    deviceCategory: 'iphone', deviceModels: [],
    repairCategory: 'repair', repairType: 'ProtectionPro Matte Film',
    description: 'Anti-glare self-healing TPU film by Madico (ProtectionPro Matte). Eliminates fingerprints and reduces screen glare.',
    basePrice: 45, rushPrice: null, priceTiers: [],
    turnaround: 'Installed at appointment', rushTurnaround: '', warranty: '90 days', mailInAvailable: false,
    internalNotes: 'ProtectionPro by Madico — Matte product line', disclaimer: '',
    tags: ['protection-pro', 'madico', 'matte', 'screen-protector', 'film', 'iphone', 'protection'],
    whatToBring: '',
    upsellIds: [],
    isUpsell: true,
  }),

  // ── iPad ──────────────────────────────────────────────────────────────────

  svc({
    id: 'ipad-screen', sku: 'IPAD-SCREEN', status: 'active',
    deviceCategory: 'ipad',
    deviceModels: [
      'iPad Pro 13" (M4)', 'iPad Pro 11" (M4)',
      'iPad Pro 12.9" (M2)', 'iPad Pro 11" (M2)',
      'iPad Air 13"', 'iPad Air 11"', 'iPad Air (5th gen)', 'iPad Air (4th gen)',
      'iPad mini (7th gen)', 'iPad mini (6th gen)', 'iPad mini (5th gen)',
      'iPad (10th gen)', 'iPad (9th gen)', 'iPad (8th gen)',
    ],
    repairCategory: 'repair', repairType: 'Screen Replacement',
    description: 'iPad screen and digitizer replacement. Restores full touch sensitivity and display quality.',
    basePrice: null, rushPrice: null,
    priceTiers: [
      { label: 'iPad Pro 13" (M4)', price: 499 },
      { label: 'iPad Pro 11" (M4)', price: 399 },
      { label: 'iPad Pro 12.9" (M2)', price: 449 },
      { label: 'iPad Pro 11" (M2)', price: 349 },
      { label: 'iPad Air 13" / 11"', price: 299 },
      { label: 'iPad Air (4th/5th gen)', price: 249 },
      { label: 'iPad mini (6th/7th gen)', price: 249 },
      { label: 'iPad mini (5th gen)', price: 199 },
      { label: 'iPad (10th gen)', price: 229 },
      { label: 'iPad (8th/9th gen)', price: 179 },
    ],
    turnaround: '1-2 days', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'iPad screens take longer — set expectations with customer.', disclaimer: 'Price varies by model.',
    tags: ['screen', 'ipad', 'display', 'digitizer'],
    whatToBring: 'iPad only. No case or accessories needed.',
    upsellIds: ['ipad-tempered-glass', 'ipad-prot-pro-ultra', 'ipad-battery'],
    isUpsell: false,
  }),

  svc({
    id: 'ipad-battery', sku: 'IPAD-BATT', status: 'active',
    deviceCategory: 'ipad', deviceModels: [],
    repairCategory: 'repair', repairType: 'Battery Replacement',
    description: 'iPad battery replacement using quality cells. Restores full day battery life.',
    basePrice: 149, rushPrice: null, priceTiers: [],
    turnaround: '1-2 days', rushTurnaround: '', warranty: '12 months', mailInAvailable: true,
    internalNotes: '', disclaimer: 'Price may vary on Pro models.',
    tags: ['battery', 'ipad', 'power'],
    whatToBring: 'iPad only.',
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'ipad-charging', sku: 'IPAD-CHRG', status: 'active',
    deviceCategory: 'ipad', deviceModels: [],
    repairCategory: 'repair', repairType: 'Charging Port Repair',
    description: 'USB-C or Lightning charging port replacement. Restores reliable charging.',
    basePrice: 99, rushPrice: null, priceTiers: [],
    turnaround: 'Same day', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '',
    tags: ['charging', 'usbc', 'lightning', 'ipad', 'port'],
    whatToBring: 'iPad only.',
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'ipad-diagnostic', sku: 'IPAD-DIAG', status: 'active',
    deviceCategory: 'ipad', deviceModels: [],
    repairCategory: 'repair', repairType: 'Diagnostic Service',
    description: 'Full iPad hardware and software diagnostic. Identifies what\'s wrong before any work begins. Fee applies toward repair.',
    basePrice: 39, rushPrice: null, priceTiers: [],
    turnaround: '30-60 minutes', rushTurnaround: '', warranty: '', mailInAvailable: true,
    internalNotes: 'Diagnostic fee credited toward repair.', disclaimer: 'Diagnostic fee credited toward repair if work is authorized.',
    tags: ['diagnostic', 'ipad'],
    whatToBring: 'iPad only. Know your Apple ID password if possible.',
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'ipad-tempered-glass', sku: 'IPAD-TGLASS', status: 'active',
    deviceCategory: 'ipad', deviceModels: [],
    repairCategory: 'repair', repairType: 'Tempered Glass Screen Protector',
    description: 'Professional tempered glass installation for iPad. Perfect add-on to a screen replacement.',
    basePrice: 30, rushPrice: null, priceTiers: [],
    turnaround: 'Instant add-on', rushTurnaround: '', warranty: '30 days', mailInAvailable: false,
    internalNotes: '', disclaimer: '',
    tags: ['screen-protector', 'tempered-glass', 'ipad', 'protection'],
    whatToBring: '',
    upsellIds: [],
    isUpsell: true,
  }),

  svc({
    id: 'ipad-prot-pro-ultra', sku: 'IPAD-PPU2', status: 'active',
    deviceCategory: 'ipad', deviceModels: [],
    repairCategory: 'repair', repairType: 'ProtectionPro Ultra 2 Film (iPad)',
    description: 'Premium self-healing TPU film by Madico for iPad. Invisible protection that self-heals minor scratches.',
    basePrice: 65, rushPrice: null, priceTiers: [],
    turnaround: 'Installed at appointment', rushTurnaround: '', warranty: '90 days', mailInAvailable: false,
    internalNotes: 'ProtectionPro by Madico — iPad sizing', disclaimer: '',
    tags: ['protection-pro', 'madico', 'screen-protector', 'film', 'ipad', 'protection'],
    whatToBring: '',
    upsellIds: [],
    isUpsell: true,
  }),

  // ── Samsung ───────────────────────────────────────────────────────────────

  svc({
    id: 'sam-screen', sku: 'SAM-SCREEN', status: 'active',
    deviceCategory: 'samsung',
    deviceModels: [
      'Galaxy S25 Ultra', 'Galaxy S25+', 'Galaxy S25',
      'Galaxy S24 Ultra', 'Galaxy S24+', 'Galaxy S24', 'Galaxy S24 FE',
      'Galaxy S23 Ultra', 'Galaxy S23+', 'Galaxy S23',
      'Galaxy S22 Ultra', 'Galaxy S22+', 'Galaxy S22',
      'Galaxy Z Fold 6', 'Galaxy Z Flip 6', 'Galaxy Z Fold 5', 'Galaxy Z Flip 5',
      'Galaxy A55', 'Galaxy A35', 'Galaxy A15',
    ],
    repairCategory: 'repair', repairType: 'Screen Replacement',
    description: 'AMOLED screen replacement for Samsung Galaxy devices. Quality display panels, proper calibration.',
    basePrice: null, rushPrice: null,
    priceTiers: [
      { label: 'Galaxy S25 Ultra', price: 349 },
      { label: 'Galaxy S25+', price: 289 },
      { label: 'Galaxy S25', price: 249 },
      { label: 'Galaxy S24 Ultra', price: 329 },
      { label: 'Galaxy S24+', price: 269 },
      { label: 'Galaxy S24', price: 229 },
      { label: 'Galaxy S23 Ultra', price: 299 },
      { label: 'Galaxy S23+', price: 249 },
      { label: 'Galaxy S23', price: 209 },
      { label: 'Galaxy S22 series', price: 189 },
      { label: 'Galaxy Z Fold / Flip', price: null },
      { label: 'Galaxy A series', price: 149 },
    ],
    turnaround: 'Same day (most models)', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Z Fold/Flip — price varies significantly. Get a quote.', disclaimer: 'Z Fold/Flip pricing requires quote. Price varies by exact model.',
    tags: ['screen', 'samsung', 'galaxy', 'amoled', 'display'],
    whatToBring: 'Samsung device only. No charger or case needed.',
    upsellIds: ['sam-prot-pro-ultra', 'sam-battery'],
    isUpsell: false,
  }),

  svc({
    id: 'sam-battery', sku: 'SAM-BATT', status: 'active',
    deviceCategory: 'samsung', deviceModels: [],
    repairCategory: 'repair', repairType: 'Battery Replacement',
    description: 'Samsung battery replacement using quality cells. Restores full battery performance.',
    basePrice: 89, rushPrice: null, priceTiers: [],
    turnaround: 'Same day', rushTurnaround: '', warranty: '12 months', mailInAvailable: true,
    internalNotes: '', disclaimer: 'Price may vary for Z Fold/Flip models.',
    tags: ['battery', 'samsung', 'galaxy', 'power'],
    whatToBring: 'Samsung device only.',
    upsellIds: ['sam-prot-pro-ultra'],
    isUpsell: false,
  }),

  svc({
    id: 'sam-charging', sku: 'SAM-CHRG', status: 'active',
    deviceCategory: 'samsung', deviceModels: [],
    repairCategory: 'repair', repairType: 'Charging Port Repair',
    description: 'USB-C charging port replacement for Samsung Galaxy devices. Resolves slow charging and connection issues.',
    basePrice: 89, rushPrice: null, priceTiers: [],
    turnaround: 'Same day', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: '', disclaimer: '',
    tags: ['charging', 'usbc', 'samsung', 'galaxy', 'port'],
    whatToBring: 'Samsung device only.',
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'sam-back-glass', sku: 'SAM-BGLASS', status: 'active',
    deviceCategory: 'samsung', deviceModels: [],
    repairCategory: 'repair', repairType: 'Back Glass Repair',
    description: 'Cracked back glass replacement for Samsung Galaxy devices.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '2-4 hours', rushTurnaround: '', warranty: '90 days', mailInAvailable: true,
    internalNotes: 'Price varies by model. Get a quote.', disclaimer: 'Price confirmed after inspection.',
    tags: ['back-glass', 'samsung', 'galaxy', 'cracked'],
    whatToBring: 'Samsung device only.',
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'sam-diagnostic', sku: 'SAM-DIAG', status: 'active',
    deviceCategory: 'samsung', deviceModels: [],
    repairCategory: 'repair', repairType: 'Diagnostic Service',
    description: 'Full hardware and software diagnostic for Samsung devices. Fee applies toward repair cost.',
    basePrice: 39, rushPrice: null, priceTiers: [],
    turnaround: '30-60 minutes', rushTurnaround: '', warranty: '', mailInAvailable: true,
    internalNotes: 'Diagnostic fee credited toward repair.', disclaimer: 'Diagnostic fee credited toward repair if work is authorized.',
    tags: ['diagnostic', 'samsung', 'galaxy'],
    whatToBring: 'Samsung device only.',
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'sam-prot-pro-ultra', sku: 'SAM-PPU2', status: 'active',
    deviceCategory: 'samsung', deviceModels: [],
    repairCategory: 'repair', repairType: 'ProtectionPro Ultra 2 Film (Samsung)',
    description: 'Premium self-healing TPU film by Madico for Samsung Galaxy devices. Curved-edge compatible.',
    basePrice: 45, rushPrice: null, priceTiers: [],
    turnaround: 'Installed at appointment', rushTurnaround: '', warranty: '90 days', mailInAvailable: false,
    internalNotes: 'ProtectionPro by Madico — Samsung sizing', disclaimer: '',
    tags: ['protection-pro', 'madico', 'screen-protector', 'samsung', 'protection'],
    whatToBring: '',
    upsellIds: [],
    isUpsell: true,
  }),

  // ── Data Recovery ─────────────────────────────────────────────────────────

  svc({
    id: 'data-recovery-phone', sku: 'DATA-PHONE', status: 'active',
    deviceCategory: 'iphone', deviceModels: [],
    repairCategory: 'repair', repairType: 'Data Recovery — iPhone / iPad',
    description: 'Attempt to recover photos, contacts, messages, and files from iPhones or iPads with water damage, failed updates, or boot loop issues. No recovery = no repair charge.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '1-3 days', rushTurnaround: '', warranty: '', mailInAvailable: true,
    internalNotes: 'Never guarantee recovery. Diagnostic first, then quote.', disclaimer: 'Data recovery success cannot be guaranteed. Pricing depends on complexity. Diagnostic fee may apply.',
    tags: ['data-recovery', 'iphone', 'ipad', 'photos', 'files'],
    whatToBring: 'Device only. Do NOT attempt to charge or restore before bringing it in.',
    upsellIds: [],
    isUpsell: false,
  }),

  svc({
    id: 'data-recovery-macbook', sku: 'DATA-MAC', status: 'active',
    deviceCategory: 'laptop-macbook', deviceModels: [],
    repairCategory: 'repair', repairType: 'Data Recovery — MacBook / SSD',
    description: 'Data recovery from MacBooks, SSDs, external drives, and flash drives with accidental deletion, no-power, or failed storage.',
    basePrice: null, rushPrice: null, priceTiers: [],
    turnaround: '1-5 days', rushTurnaround: '', warranty: '', mailInAvailable: true,
    internalNotes: 'Never guarantee recovery. Diagnostic first, then quote.', disclaimer: 'Data recovery success cannot be guaranteed. Pricing depends on severity and storage type.',
    tags: ['data-recovery', 'macbook', 'ssd', 'external', 'files'],
    whatToBring: 'MacBook or storage device only. Do NOT attempt DIY recovery before bringing it in.',
    upsellIds: [],
    isUpsell: false,
  }),
];
